import { Download, Printer } from 'lucide-react';

const VENDOR_COLORS = ['text-emerald-400', 'text-blue-400', 'text-purple-400'];
const VENDOR_BG     = ['bg-emerald-500',   'bg-blue-500',   'bg-purple-500'];
const WEIGHT_LABELS = { 1: 'Nice to Have', 2: 'Important', 3: 'Must Have' };

/**
 * Step 5 — Scoring results table with export and print actions.
 * @param {{ template, selectedFeatures, vendors, featureWeights, ratings, comments, scores, maxPossible, onExport, onReset }} props
 */
export function Results({ template, selectedFeatures, vendors, featureWeights, ratings, comments, scores, maxPossible, onExport, onReset }) {
  const topScore  = Math.max(...scores);
  const winnerIdx = scores.indexOf(topScore);

  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="text-base md:text-lg font-black text-white mb-2 tracking-tight">Your Comparison Results</h2>
        <p className="text-slate-400 text-sm">{template.meta.title} · {selectedFeatures.length} features rated · Max possible: {maxPossible} pts</p>
      </div>

      {/* Score summary cards */}
      <div className="grid sm:grid-cols-3 gap-5 mb-10">
        {vendors.map((v, idx) => {
          const s    = scores[idx];
          const pct  = topScore > 0 ? Math.round((s / topScore) * 100) : 0;
          const isWinner = idx === winnerIdx && s > 0;
          return (
            <div key={idx} data-testid={`result-vendor-${idx}`}
              className={`rounded-2xl border p-6 text-center transition-all
                ${isWinner ? 'border-emerald-500 bg-emerald-950/30 shadow-lg shadow-emerald-900/20' : 'border-slate-700 bg-slate-800'}`}>
              {isWinner && <div className="text-xs text-emerald-400 font-bold tracking-widest mb-2 uppercase">Highest Score</div>}
              <div className={`text-sm font-bold mb-1 ${VENDOR_COLORS[idx]}`}>{v.name || `Vendor ${idx + 1}`}</div>
              {v.product_name && <div className="text-xs text-slate-500 mb-2">{v.product_name}</div>}
              <div className="text-4xl font-black text-white mb-1">{s}</div>
              <div className="text-xs text-slate-500">out of {maxPossible}</div>
              <div className="mt-4 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${isWinner ? 'bg-emerald-500' : VENDOR_BG[idx]}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature breakdown */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden mb-8" data-testid="results-table">
        <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-300">Feature Breakdown</h3>
          <span className="text-xs text-slate-500">Rating × Weight = Weighted Score</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-900/40">
                <th className="text-left px-5 py-3 text-slate-400 font-semibold">Feature</th>
                <th className="text-center px-3 py-3 text-slate-400 font-semibold">Wt</th>
                {vendors.map((v, i) => (
                  <th key={i} className={`text-center px-3 py-3 font-semibold ${VENDOR_COLORS[i]}`}>{v.name || `V${i + 1}`}</th>
                ))}
                <th className="text-left px-4 py-3 text-slate-500 font-semibold text-xs">Notes</th>
              </tr>
            </thead>
            <tbody>
              {selectedFeatures.map((f, fi) => {
                const w         = featureWeights[f.id] ?? f.default_weight;
                const fRatings  = ratings[f.id] || [0, 0, 0];
                const weighted  = fRatings.map(r => r * w);
                const topW      = Math.max(...weighted);
                return (
                  <tr key={f.id} data-testid={`result-row-${f.id}`}
                    className={`border-b border-slate-700/40 ${fi % 2 === 0 ? '' : 'bg-slate-900/20'}`}>
                    <td className="px-5 py-3">
                      <span className="text-slate-200 font-medium">{f.title}</span>
                      {f.is_vendor_sponsored && <span className="ml-2 text-xs text-amber-500 opacity-70">★</span>}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded
                        ${w === 3 ? 'bg-red-900/50 text-red-300' : w === 2 ? 'bg-amber-900/50 text-amber-300' : 'bg-slate-700 text-slate-400'}`}>×{w}</span>
                    </td>
                    {fRatings.map((r, vIdx) => {
                      const ws = r * w;
                      const isTop = ws === topW && ws > 0;
                      return (
                        <td key={vIdx} className={`px-3 py-3 text-center font-mono ${isTop ? 'text-emerald-400 font-bold' : 'text-slate-300'}`}>
                          {r > 0 ? <>{r}<span className="text-slate-600 text-xs"> → </span><span className={isTop ? 'text-emerald-400' : 'text-slate-400'}>{ws}</span></> : <span className="text-slate-700">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[160px] truncate">{comments[f.id] || ''}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-600 bg-slate-900/40">
                <td className="px-5 py-4 font-black text-white text-sm" colSpan={2}>TOTAL SCORE</td>
                {scores.map((s, i) => (
                  <td key={i} data-testid={`total-score-${i}`}
                    className={`px-3 py-4 text-center text-xl font-black ${i === winnerIdx && s > 0 ? 'text-emerald-400' : 'text-slate-300'}`}>{s}</td>
                ))}
                <td className="px-4 py-4" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-600 text-center mb-6">★ = vendor-sponsored feature · All ratings and weights were set by you</p>

      <div className="flex items-center justify-center gap-4 flex-wrap">
        <button type="button" data-testid="export-csv" onClick={onExport}
          className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-emerald-900/30">
          <Download size={18} /> Export as CSV
        </button>
        <button type="button" data-testid="print-results" onClick={() => window.print()}
          className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all">
          <Printer size={18} /> Print
        </button>
        <button type="button" data-testid="start-over" onClick={onReset}
          className="text-slate-400 hover:text-slate-200 px-6 py-3 rounded-xl font-semibold transition-colors">
          Start Over
        </button>
      </div>
    </div>
  );
}
